/**
 * Formato paraguayo. Está en un solo lugar porque un importe mal formateado en una
 * pantalla y bien en otra es la clase de detalle que le hace perder confianza al usuario.
 */

const LOCAL = 'es-PY';

const guaranies = new Intl.NumberFormat(LOCAL, {
  maximumFractionDigits: 0,
  useGrouping: true,
});

const fechaCorta = new Intl.DateTimeFormat(LOCAL, {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const fechaLarga = new Intl.DateTimeFormat(LOCAL, {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});

const horaCorta = new Intl.DateTimeFormat(LOCAL, {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/** El guaraní no tiene decimales: se muestra entero y con puntos de miles. */
export function gs(monto: number | null | undefined): string {
  if (monto == null) {
    return '—';
  }

  return `${guaranies.format(monto)} Gs`;
}

/** Importe sin la unidad, para columnas donde el encabezado ya dice «Gs». */
export function numero(valor: number | null | undefined): string {
  return valor == null ? '—' : guaranies.format(valor);
}

export function porcentaje(valor: number | null | undefined, decimales = 1): string {
  return valor == null ? '—' : `${valor.toFixed(decimales).replace('.', ',')} %`;
}

/**
 * Las fechas sin hora (`DateOnly`) llegan como `2026-09-07`. Construirlas con `new Date`
 * las interpreta en UTC y en Paraguay se corren un día para atrás, así que se parsean a
 * mano.
 */
export function fechaDeIso(iso: string): Date {
  const soloFecha = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);

  if (soloFecha) {
    return new Date(Number(soloFecha[1]), Number(soloFecha[2]) - 1, Number(soloFecha[3]));
  }

  return new Date(iso);
}

export function fecha(iso: string | null | undefined): string {
  return iso ? fechaCorta.format(fechaDeIso(iso)) : '—';
}

export function fechaConDia(iso: string | null | undefined): string {
  return iso ? fechaLarga.format(fechaDeIso(iso)) : '—';
}

export function hora(iso: string | null | undefined): string {
  return iso ? horaCorta.format(fechaDeIso(iso)) : '—';
}

export function fechaYHora(iso: string | null | undefined): string {
  return iso ? `${fecha(iso)} ${hora(iso)}` : '—';
}

/** Día de hoy en formato ISO, para los parámetros de la API. */
export function hoyIso(): string {
  const ahora = new Date();
  const mes = `${ahora.getMonth() + 1}`.padStart(2, '0');
  const dia = `${ahora.getDate()}`.padStart(2, '0');

  return `${ahora.getFullYear()}-${mes}-${dia}`;
}

export function isoDe(dia: Date): string {
  const mes = `${dia.getMonth() + 1}`.padStart(2, '0');
  const numeroDia = `${dia.getDate()}`.padStart(2, '0');

  return `${dia.getFullYear()}-${mes}-${numeroDia}`;
}

export function sumarDias(iso: string, dias: number): string {
  const base = fechaDeIso(iso);
  base.setDate(base.getDate() + dias);

  return isoDe(base);
}

/** Primer día del mes de una fecha, para los rangos de los reportes. */
export function inicioDeMes(iso: string): string {
  const base = fechaDeIso(iso);

  return isoDe(new Date(base.getFullYear(), base.getMonth(), 1));
}

/**
 * Los enumerados del servidor llegan en PascalCase (`ControlPeriodico`, `PorVencer`). Se
 * separan en palabras para que la interfaz no muestre jerga de programación.
 */
export function legible(valor: string | null | undefined): string {
  if (!valor) {
    return '—';
  }

  const separado = valor.replace(/([a-záéíóúñ])([A-ZÁÉÍÓÚÑ])/g, '$1 $2').replace(/_/g, ' ');

  return separado.charAt(0).toUpperCase() + separado.slice(1).toLowerCase();
}

/** Iniciales para el avatar del paciente o del profesional. */
export function iniciales(nombre: string): string {
  return nombre
    .replace(/,/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((parte) => parte.charAt(0).toUpperCase())
    .join('');
}

/** Días de atraso en palabras: «hoy», «ayer», «12 días». */
export function atraso(dias: number): string {
  if (dias <= 0) {
    return 'al día';
  }

  if (dias === 1) {
    return '1 día';
  }

  return `${dias} días`;
}
